/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import SearchInput from "components/SearchInput";
import usePaginationHook from "hooks/usePagination";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import configData from "config/config.json";
import { platformTableColumns } from "utils/tableHelper/pobTableHelper";
import {
  deletePlatform,
  getPlatforms,
  updatePlatformStatus,
} from "services/platformService";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import { sortArray } from "utils/miscUtils";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useConsent from "hooks/useConsent";

const useStyles = makeStyles((theme: any) => ({
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
}));

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDisable?: (id: any) => void;
    handleDelete?: (data: any) => void;
  };
}

const RowActions = ({ rowId, data, actionHandlers }: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const { handleDisable = () => {}, handleDelete = () => {} } = actionHandlers;

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEditClick = (event: any, data: any) => {
    handleClose(event);
    navigate(configData.routes.platforms.edit?.replace(":id", data?._id));
  };

  const handleDisableClick = (event: any, id: any) => {
    handleClose(event);
    handleDisable(id);
  };

  const handleDeleteClick = (event: any, rowData: any) => {
    handleClose(event);
    handleDelete(rowData);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.PLATFORM.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={(e) => handleEditClick(e, data)}>Edit</MenuItem>
        <MenuItem onClick={(e) => handleDisableClick(e, data?._id)}>
          {data?.isActive ? "Disable" : "Enable"}
        </MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={(e) => handleDeleteClick(e, data)}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const Platforms = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLATFORM.UPDATE_ROLES,
  );

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async (search?: any) => {
    setLoading(true);
    try {
      let payload = { pageNo, pageSize };
      const searchParam = search !== undefined ? search : searchValue;
      if (searchParam) payload["search"] = searchParam;
      const resp = await getPlatforms(payload);
      setFilteredList(
        resp?.data?.platforms?.map((el) => ({
          ...el,
          isActiveText: el?.isActive ? "YES" : "NO",
          rowDisabled: !el?.isActive,
        })),
      );
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onSearch = (value: any) => {
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.platformName ?? data?._id, id: data?._id });
  };

  const onDelete = async () => {
    try {
      const resp = await deletePlatform(constentDetails?.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message);
    }
  };

  const onDisable = async (platformId) => {
    try {
      const resp = await updatePlatformStatus(platformId);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
    } catch (error) {
      showSnackbar(error.message);
    }
  };

  const createPlatform = () => navigate(configData.routes.platforms.create);

  const rowHref = (data: any) =>
    hasEditPermission
      ? configData.routes.platforms.edit?.replace(":id", data?._id)
      : null;

  useEffect(() => {
    const initialColumns = platformTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission,
    );
    setColumns(initialColumns);

    fetchData(searchValue);
  }, [pageNo, pageSize, searchValue]);

  return (
    <ContentShell
      title="Platforms"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a platform"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.LEAGUE.CREATE_ROLES) && (
            <CustomButton onClick={createPlatform}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          actions={RowActions}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          rowHref={rowHref}
          highlightAltRow={true}
          actionHandlers={{
            handleDelete: _openConsentDialog,
            handleDisable: onDisable,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails?.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default Platforms;
